/**
 * Centralized Article Configuration
 * 
 * Maps article IDs (URL slugs) to their components and metadata.
 * This enables the ArticleDetailPage to dynamically load articles
 * and automatically inject Schema.org structured data.
 * 
 * Architecture Pattern:
 * - Similar to ToolDetailPage.tsx approach
 * - Single source of truth for all articles
 * - Type-safe configuration with TypeScript
 * - Lazy loading enabled for better performance (reduces initial bundle by ~500KB)
 * 
 * Usage:
 * URL: /resources/compound-interest-article
 * → ArticleDetailPage uses id "compound-interest-article"
 * → Loads CompoundInterestArticle component lazily
 * → Automatically injects Article schema
 */

import React from 'react';

// Lazy load article components for better performance
const CompoundInterestArticle = React.lazy(() => import('../components/articles/CompoundInterestArticle'));
const RetirementPlanningArticle = React.lazy(() => import('../components/articles/RetirementPlanningArticle'));
const EmergencyFundArticle = React.lazy(() => import('../components/articles/EmergencyFundArticle'));
const RiskReturnArticle = React.lazy(() => import('../components/articles/RiskReturnArticle'));
const StartInvestingArticle = React.lazy(() => import('../components/articles/StartInvestingArticle'));
const DCAVsLumpSumArticle = React.lazy(() => import('../components/articles/DCAVsLumpSumArticle'));
const InvestingMistakesArticle = React.lazy(() => import('../components/articles/InvestingMistakesArticle'));
const ETFIndexFundsArticle = React.lazy(() => import('../components/articles/ETFIndexFundsArticle'));
const DividendReinvestmentArticle = React.lazy(() => import('../components/articles/DividendReinvestmentArticle'));
const CryptoArticle = React.lazy(() => import('../components/articles/CryptoArticle'));
const RentingVsBuyingArticle = React.lazy(() => import('../components/articles/RentingVsBuyingArticle'));
const MortgageRenegotiationArticle = React.lazy(() => import('../components/articles/MortgageRenegotiationArticle'));
const FirstSalaryArticle = React.lazy(() => import('../components/articles/FirstSalaryArticle'));
const SalaryNegotiationArticle = React.lazy(() => import('../components/articles/SalaryNegotiationArticle'));
const SalaryNegotiationWorthArticle = React.lazy(() => import('../components/articles/SalaryNegotiationWorthArticle'));
const IRPFGuideArticle = React.lazy(() => import('../components/articles/IRPFGuideArticle'));
const CryptoTaxGuideArticle = React.lazy(() => import('../components/articles/CryptoTaxGuideArticle'));
const DigitalNomadTaxGuideArticle = React.lazy(() => import('../components/articles/DigitalNomadTaxGuideArticle'));
const SelfEmployedFinancesArticle = React.lazy(() => import('../components/articles/SelfEmployedFinancesArticle'));
const InflationArticle = React.lazy(() => import('../components/articles/InflationArticle'));
const InflationSavingsStrategiesArticle = React.lazy(() => import('../components/articles/InflationSavingsStrategiesArticle'));
const UtilityBillsArticle = React.lazy(() => import('../components/articles/UtilityBillsArticle'));
const InvisibleExpensesArticle = React.lazy(() => import('../components/articles/InvisibleExpensesArticle'));
const ImpulseBuyingArticle = React.lazy(() => import('../components/articles/ImpulseBuyingArticle'));
const BNPLArticle = React.lazy(() => import('../components/articles/BNPLArticle'));
const LifestyleInflationArticle = React.lazy(() => import('../components/articles/LifestyleInflationArticle'));
const OnePercentRuleArticle = React.lazy(() => import('../components/articles/OnePercentRuleArticle'));
const LowSalarySavingStrategiesArticle = React.lazy(() => import('../components/articles/LowSalarySavingStrategiesArticle'));
const MonthlySavingChallengesArticle = React.lazy(() => import('../components/articles/MonthlySavingChallengesArticle'));
const HowMuchToRetireArticle = React.lazy(() => import('../components/articles/HowMuchToRetireArticle'));
const FinancialIndependenceRealisticPlanArticle = React.lazy(() => import('../components/articles/FinancialIndependenceRealisticPlanArticle'));
const RealCostNeverInvestingArticle = React.lazy(() => import('../components/articles/RealCostNeverInvestingArticle'));
const FearOfInvestingArticle = React.lazy(() => import('../components/articles/FearOfInvestingArticle'));
const InvestingWith100Article = React.lazy(() => import('../components/articles/InvestingWith100Article'));
const WhyTradersLoseMoneyArticle = React.lazy(() => import('../components/articles/WhyTradersLoseMoneyArticle'));
const RoboadvisorsVsHumanAdvisorsArticle = React.lazy(() => import('../components/articles/RoboadvisorsVsHumanAdvisorsArticle'));
const ESGGreenwashingArticle = React.lazy(() => import('../components/articles/ESGGreenwashingArticle'));
const AIFinanceArticle = React.lazy(() => import('../components/articles/AIFinanceArticle'));
const AIInvestmentArticle = React.lazy(() => import('../components/articles/AIInvestmentArticle'));
const DigitalScamsArticle = React.lazy(() => import('../components/articles/DigitalScamsArticle'));
const AutomateFinancesArticle = React.lazy(() => import('../components/articles/AutomateFinancesArticle'));
const CouplesFinanceArticle = React.lazy(() => import('../components/articles/CouplesFinanceArticle'));
const FinancialPlanningBeforeChildrenArticle = React.lazy(() => import('../components/articles/FinancialPlanningBeforeChildrenArticle'));
const PreparingFinancesBeforeParenthoodArticle = React.lazy(() => import('../components/articles/PreparingFinancesBeforeParenthoodArticle'));
const BeforeQuittingJobArticle = React.lazy(() => import('../components/articles/BeforeQuittingJobArticle'));

/**
 * Article configuration interface
 */
export interface ArticleConfig {
  /** Article ID (matches URL slug) */
  id: string;
  
  /** React component to render */
  component: React.ComponentType<any>;
  
  /** Article metadata key in i18n for title/description */
  metaKey: string;
  
  /** Publication date (ISO format) */
  datePublished: string;
  
  /** Last modified date (ISO format) */
  dateModified: string;
  
  /** Keywords for Schema.org */
  keywords: string[];
  
  /** Featured image URL (optional) */
  imageUrl?: string;
}

/**
 * All article configurations
 * Add new articles here to automatically get:
 * - Dynamic routing
 * - Schema.org structured data
 * - Pre-rendering support
 */
export const ARTICLE_CONFIGS: Record<string, ArticleConfig> = {
  'compound-interest-article': {
    id: 'compound-interest-article',
    component: CompoundInterestArticle,
    metaKey: 'compoundInterestArticle',
    datePublished: '2024-01-15',
    dateModified: '2024-12-26',
    keywords: ['compound interest', 'investing', 'finance', 'savings', 'growth'],
    imageUrl: '/images/compound-interest-og.jpg',
  },
  
  'retirement-planning-article': {
    id: 'retirement-planning-article',
    component: RetirementPlanningArticle,
    metaKey: 'retirementPlanning',
    datePublished: '2024-01-20',
    dateModified: '2024-12-26',
    keywords: ['retirement', 'planning', 'pension', 'savings', 'financial independence'],
  },
  
  'emergency-fund-article': {
    id: 'emergency-fund-article',
    component: EmergencyFundArticle,
    metaKey: 'emergencyFund',
    datePublished: '2024-02-01',
    dateModified: '2024-12-26',
    keywords: ['emergency fund', 'savings', 'financial security', 'budgeting'],
  },
  
  'risk-return-article': {
    id: 'risk-return-article',
    component: RiskReturnArticle,
    metaKey: 'riskReturn',
    datePublished: '2024-02-10',
    dateModified: '2024-12-26',
    keywords: ['risk', 'return', 'investing', 'portfolio', 'diversification'],
  },
  
  'start-investing-article': {
    id: 'start-investing-article',
    component: StartInvestingArticle,
    metaKey: 'startInvesting',
    datePublished: '2024-02-15',
    dateModified: '2024-12-26',
    keywords: ['investing for beginners', 'start investing', 'stocks', 'portfolio'],
  },
  
  'dca-vs-lump-sum-article': {
    id: 'dca-vs-lump-sum-article',
    component: DCAVsLumpSumArticle,
    metaKey: 'dcaVsLumpSum',
    datePublished: '2024-03-01',
    dateModified: '2024-12-26',
    keywords: ['dollar cost averaging', 'DCA', 'lump sum', 'investing strategy'],
  },
  
  'investing-mistakes-article': {
    id: 'investing-mistakes-article',
    component: InvestingMistakesArticle,
    metaKey: 'investingMistakes',
    datePublished: '2024-03-10',
    dateModified: '2024-12-26',
    keywords: ['investing mistakes', 'common errors', 'investment advice'],
  },
  
  'etf-index-funds-article': {
    id: 'etf-index-funds-article',
    component: ETFIndexFundsArticle,
    metaKey: 'etfIndexFunds',
    datePublished: '2024-03-20',
    dateModified: '2024-12-26',
    keywords: ['ETF', 'index funds', 'passive investing', 'portfolio'],
  },
  
  'dividend-reinvestment-article': {
    id: 'dividend-reinvestment-article',
    component: DividendReinvestmentArticle,
    metaKey: 'dividendReinvestment',
    datePublished: '2024-04-01',
    dateModified: '2024-12-26',
    keywords: ['dividends', 'reinvestment', 'passive income', 'DRIP'],
  },
  
  'crypto-article': {
    id: 'crypto-article',
    component: CryptoArticle,
    metaKey: 'crypto',
    datePublished: '2024-04-10',
    dateModified: '2024-12-26',
    keywords: ['cryptocurrency', 'bitcoin', 'blockchain', 'digital assets'],
  },
  
  'renting-vs-buying-article': {
    id: 'renting-vs-buying-article',
    component: RentingVsBuyingArticle,
    metaKey: 'rentingVsBuying',
    datePublished: '2024-04-20',
    dateModified: '2024-12-26',
    keywords: ['rent', 'buy', 'homeownership', 'real estate', 'housing'],
  },
  
  'mortgage-renegotiation-article': {
    id: 'mortgage-renegotiation-article',
    component: MortgageRenegotiationArticle,
    metaKey: 'mortgageRenegotiation',
    datePublished: '2024-05-01',
    dateModified: '2024-12-26',
    keywords: ['mortgage', 'renegotiation', 'refinancing', 'home loan'],
  },
  
  'first-salary-article': {
    id: 'first-salary-article',
    component: FirstSalaryArticle,
    metaKey: 'firstSalary',
    datePublished: '2024-05-10',
    dateModified: '2024-12-26',
    keywords: ['first salary', 'financial planning', 'budgeting', 'saving'],
  },
  
  'salary-negotiation-article': {
    id: 'salary-negotiation-article',
    component: SalaryNegotiationArticle,
    metaKey: 'salaryNegotiation',
    datePublished: '2024-05-20',
    dateModified: '2024-12-26',
    keywords: ['salary negotiation', 'career', 'raise', 'compensation'],
  },
  
  'salary-negotiation-worth-article': {
    id: 'salary-negotiation-worth-article',
    component: SalaryNegotiationWorthArticle,
    metaKey: 'salaryNegotiationWorth',
    datePublished: '2024-06-01',
    dateModified: '2024-12-26',
    keywords: ['salary worth', 'compensation', 'pay raise', 'career growth'],
  },
  
  'irpf-guide-article': {
    id: 'irpf-guide-article',
    component: IRPFGuideArticle,
    metaKey: 'irpfGuide',
    datePublished: '2024-06-10',
    dateModified: '2024-12-26',
    keywords: ['IRPF', 'taxes', 'Spain', 'income tax', 'tax guide'],
  },
  
  'crypto-tax-guide-article': {
    id: 'crypto-tax-guide-article',
    component: CryptoTaxGuideArticle,
    metaKey: 'cryptoTaxGuide',
    datePublished: '2024-06-20',
    dateModified: '2024-12-26',
    keywords: ['crypto taxes', 'cryptocurrency', 'tax guide', 'bitcoin tax'],
  },
  
  'digital-nomad-tax-guide-article': {
    id: 'digital-nomad-tax-guide-article',
    component: DigitalNomadTaxGuideArticle,
    metaKey: 'digitalNomadTaxGuide',
    datePublished: '2024-07-01',
    dateModified: '2024-12-26',
    keywords: ['digital nomad', 'taxes', 'remote work', 'expat', 'international tax'],
  },
  
  'self-employed-finances-article': {
    id: 'self-employed-finances-article',
    component: SelfEmployedFinancesArticle,
    metaKey: 'selfEmployedFinances',
    datePublished: '2024-07-10',
    dateModified: '2024-12-26',
    keywords: ['self-employed', 'freelance', 'finances', 'taxes', 'business'],
  },
  
  'inflation-article': {
    id: 'inflation-article',
    component: InflationArticle,
    metaKey: 'inflation',
    datePublished: '2024-07-20',
    dateModified: '2024-12-26',
    keywords: ['inflation', 'purchasing power', 'economics', 'cost of living'],
  },
  
  'inflation-savings-strategies-article': {
    id: 'inflation-savings-strategies-article',
    component: InflationSavingsStrategiesArticle,
    metaKey: 'inflationSavingsStrategies',
    datePublished: '2024-08-01',
    dateModified: '2024-12-26',
    keywords: ['inflation', 'savings', 'strategies', 'protect money', 'investing'],
  },
  
  'utility-bills-article': {
    id: 'utility-bills-article',
    component: UtilityBillsArticle,
    metaKey: 'utilityBills',
    datePublished: '2024-08-10',
    dateModified: '2024-12-26',
    keywords: ['utility bills', 'savings', 'budgeting', 'expenses', 'reduce costs'],
  },
  
  'invisible-expenses-article': {
    id: 'invisible-expenses-article',
    component: InvisibleExpensesArticle,
    metaKey: 'invisibleExpenses',
    datePublished: '2024-08-20',
    dateModified: '2024-12-26',
    keywords: ['hidden expenses', 'budgeting', 'financial planning', 'spending'],
  },
  
  'impulse-buying-article': {
    id: 'impulse-buying-article',
    component: ImpulseBuyingArticle,
    metaKey: 'impulseBuying',
    datePublished: '2024-09-01',
    dateModified: '2024-12-26',
    keywords: ['impulse buying', 'spending habits', 'budgeting', 'self-control'],
  },
  
  'bnpl-article': {
    id: 'bnpl-article',
    component: BNPLArticle,
    metaKey: 'bnpl',
    datePublished: '2024-09-10',
    dateModified: '2024-12-26',
    keywords: ['buy now pay later', 'BNPL', 'credit', 'debt', 'financial planning'],
  },
  
  'lifestyle-inflation-article': {
    id: 'lifestyle-inflation-article',
    component: LifestyleInflationArticle,
    metaKey: 'lifestyleInflation',
    datePublished: '2024-09-20',
    dateModified: '2024-12-26',
    keywords: ['lifestyle inflation', 'spending creep', 'budgeting', 'savings'],
  },
  
  'one-percent-rule-article': {
    id: 'one-percent-rule-article',
    component: OnePercentRuleArticle,
    metaKey: 'onePercentRule',
    datePublished: '2024-10-01',
    dateModified: '2024-12-26',
    keywords: ['1% rule', 'real estate', 'investing', 'rental property'],
  },
  
  'low-salary-saving-strategies-article': {
    id: 'low-salary-saving-strategies-article',
    component: LowSalarySavingStrategiesArticle,
    metaKey: 'lowSalarySavingStrategies',
    datePublished: '2024-10-10',
    dateModified: '2024-12-26',
    keywords: ['low income', 'saving strategies', 'budgeting', 'financial tips'],
  },
  
  'monthly-saving-challenges-article': {
    id: 'monthly-saving-challenges-article',
    component: MonthlySavingChallengesArticle,
    metaKey: 'monthlySavingChallenges',
    datePublished: '2024-10-20',
    dateModified: '2024-12-26',
    keywords: ['saving challenges', 'budgeting', 'financial goals', 'motivation'],
  },
  
  'how-much-to-retire-article': {
    id: 'how-much-to-retire-article',
    component: HowMuchToRetireArticle,
    metaKey: 'howMuchToRetire',
    datePublished: '2024-11-01',
    dateModified: '2024-12-26',
    keywords: ['retirement', 'planning', 'savings', 'financial independence'],
  },
  
  'financial-independence-realistic-plan-article': {
    id: 'financial-independence-realistic-plan-article',
    component: FinancialIndependenceRealisticPlanArticle,
    metaKey: 'financialIndependenceRealisticPlan',
    datePublished: '2024-11-10',
    dateModified: '2024-12-26',
    keywords: ['FIRE', 'financial independence', 'retirement', 'planning'],
  },
  
  'real-cost-never-investing-article': {
    id: 'real-cost-never-investing-article',
    component: RealCostNeverInvestingArticle,
    metaKey: 'realCostNeverInvesting',
    datePublished: '2024-11-20',
    dateModified: '2024-12-26',
    keywords: ['investing', 'opportunity cost', 'wealth building', 'savings'],
  },
  
  'fear-of-investing-article': {
    id: 'fear-of-investing-article',
    component: FearOfInvestingArticle,
    metaKey: 'fearOfInvesting',
    datePublished: '2024-12-01',
    dateModified: '2024-12-26',
    keywords: ['investing fear', 'risk aversion', 'financial psychology', 'beginner investing'],
  },
  
  'investing-with-100-article': {
    id: 'investing-with-100-article',
    component: InvestingWith100Article,
    metaKey: 'investingWith100',
    datePublished: '2024-12-05',
    dateModified: '2024-12-26',
    keywords: ['small investments', 'beginner investing', 'low capital', 'start investing'],
  },
  
  'why-traders-lose-money-article': {
    id: 'why-traders-lose-money-article',
    component: WhyTradersLoseMoneyArticle,
    metaKey: 'whyTradersLoseMoney',
    datePublished: '2024-12-10',
    dateModified: '2024-12-26',
    keywords: ['trading', 'losses', 'investing mistakes', 'day trading'],
  },
  
  'roboadvisors-vs-human-advisors-article': {
    id: 'roboadvisors-vs-human-advisors-article',
    component: RoboadvisorsVsHumanAdvisorsArticle,
    metaKey: 'roboadvisorsVsHumanAdvisors',
    datePublished: '2024-12-15',
    dateModified: '2024-12-26',
    keywords: ['robo-advisors', 'financial advisors', 'investment management', 'automated investing'],
  },
  
  'esg-greenwashing-article': {
    id: 'esg-greenwashing-article',
    component: ESGGreenwashingArticle,
    metaKey: 'esgGreenwashing',
    datePublished: '2024-12-18',
    dateModified: '2024-12-26',
    keywords: ['ESG', 'greenwashing', 'sustainable investing', 'ethical investing'],
  },
  
  'ai-finance-article': {
    id: 'ai-finance-article',
    component: AIFinanceArticle,
    metaKey: 'aiFinance',
    datePublished: '2024-12-20',
    dateModified: '2024-12-26',
    keywords: ['AI', 'artificial intelligence', 'finance', 'fintech', 'automation'],
  },
  
  'ai-investment-article': {
    id: 'ai-investment-article',
    component: AIInvestmentArticle,
    metaKey: 'aiInvestment',
    datePublished: '2024-12-21',
    dateModified: '2024-12-26',
    keywords: ['AI investing', 'algorithmic trading', 'robo-advisors', 'machine learning'],
  },
  
  'digital-scams-article': {
    id: 'digital-scams-article',
    component: DigitalScamsArticle,
    metaKey: 'digitalScams',
    datePublished: '2024-12-22',
    dateModified: '2024-12-26',
    keywords: ['scams', 'fraud', 'cybersecurity', 'financial security', 'phishing'],
  },
  
  'automate-finances-article': {
    id: 'automate-finances-article',
    component: AutomateFinancesArticle,
    metaKey: 'automateFinances',
    datePublished: '2024-12-23',
    dateModified: '2024-12-26',
    keywords: ['financial automation', 'budgeting', 'savings', 'bill pay', 'fintech'],
  },
  
  'couples-finance-article': {
    id: 'couples-finance-article',
    component: CouplesFinanceArticle,
    metaKey: 'couplesFinance',
    datePublished: '2024-12-24',
    dateModified: '2024-12-26',
    keywords: ['couples', 'finance', 'budgeting', 'joint accounts', 'financial planning'],
  },
  
  'financial-planning-before-children-article': {
    id: 'financial-planning-before-children-article',
    component: FinancialPlanningBeforeChildrenArticle,
    metaKey: 'financialPlanningBeforeChildren',
    datePublished: '2024-12-25',
    dateModified: '2024-12-26',
    keywords: ['family planning', 'financial planning', 'children', 'budgeting', 'savings'],
  },
  
  'preparing-finances-before-parenthood-article': {
    id: 'preparing-finances-before-parenthood-article',
    component: PreparingFinancesBeforeParenthoodArticle,
    metaKey: 'preparingFinancesBeforeParenthood',
    datePublished: '2024-12-25',
    dateModified: '2024-12-26',
    keywords: ['parenthood', 'financial preparation', 'family budget', 'savings'],
  },
  
  'before-quitting-job-article': {
    id: 'before-quitting-job-article',
    component: BeforeQuittingJobArticle,
    metaKey: 'beforeQuittingJob',
    datePublished: '2024-12-26',
    dateModified: '2024-12-26',
    keywords: ['career change', 'financial planning', 'emergency fund', 'job transition'],
  },
};

/**
 * Get all article IDs (for route generation)
 */
export function getArticleIds(): string[] {
  return Object.keys(ARTICLE_CONFIGS);
}

/**
 * Get article configuration by ID
 */
export function getArticleConfig(id: string): ArticleConfig | undefined {
  return ARTICLE_CONFIGS[id];
}
